import { Chip, ScrollShadow, Spinner } from '@heroui/react';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { isSelfPickup } from '@/components/OrderLocation';
import {
  useInAppNotifications, useLoadMoreOnScroll, useMarkNotificationAsRead, useWebPushPermission,
} from '@/hooks';
import { useOrganizationState, useUserState } from '@/state';
import { InAppNotification } from '@/types/inAppNotification.types';
import { mapInAppNotificationToUI } from '@/utils';
import { useLocaleNavigate } from '@/utils/locale';

import { TableEmptyState } from '../TableEmptyState';
import {
  FooterButton,
  FooterGhostButton,
  IconWrapper,
  LoadMoreButton,
  LoadMoreRow,
  NotificationContent,
  NotificationDescription,
  NotificationFooter,
  NotificationHeader,
  NotificationItem,
  NotificationList,
  NotificationTime,
  NotificationTitle,
  NotificationWrapper,
  PermissionButton,
  PermissionPrompt,
  PickupTag,
  RoomNumber,
  UnreadBadge,
} from './Notyfication.styled';

interface NotificationProps {
  showTitle?: boolean;
  maxHeight?: string;
  showFooter?: boolean;
  fullWidth?: boolean;
  daysLimit?: number;
  showOnlyUnread?: boolean;
  // true → lista rośnie naturalnie i scroll dzieje się na stronie (centrum
  // powiadomień); false → scroll wewnątrz boxa o wysokości maxHeight (popover).
  pageScroll?: boolean;
  // true → przezroczyste tło + czarny font (widok centrum powiadomień na stronie).
  transparent?: boolean;
  // true → zamiast doczytywania przy scrollu pokazujemy przycisk „Załaduj więcej".
  loadMoreButton?: boolean;
}

export const Notification: FC<NotificationProps> = ({
  showTitle = false,
  maxHeight = '400px',
  showFooter = false,
  fullWidth = false,
  daysLimit,
  showOnlyUnread = false,
  pageScroll = false,
  transparent = false,
  loadMoreButton = false,
}) => {
  const { t } = useTranslation();
  const navigate = useLocaleNavigate();
  const { currentUser, userLanguage } = useUserState();
  // Monit pokazujemy tylko przy stanie `default` — po odmowie przeglądarka nie zapyta
  // ponownie, a martwy przycisk wyglądałby na zepsuty panel.
  const { canAsk: canAskForNotifications, request: requestNotifications } = useWebPushPermission(currentUser?.uid);
  const { organizationId, isLoading: isOrgLoading } = useOrganizationState();

  // Infinite scroll — listę odsłaniamy po stronie klienta (dane są już pobrane
  // snapshotem). feedRef wskazuje kontener, którego scroll obserwujemy.
  const [displayCount, setDisplayCount] = useState(10);
  const feedRef = useRef<HTMLDivElement>(null);

  // Paginacja SERWEROWA (loadMoreButton): pobieramy z Firebase tylko `serverLimit`
  // dokumentów; „Załaduj więcej" zwiększa limit o 10 → hook re-subskrybuje snapshot
  // z nowym `limit()`, czyli kolejny request do Firestore (bez ciągnięcia wszystkiego).
  const PAGE_SIZE = 10;
  const [serverLimit, setServerLimit] = useState(PAGE_SIZE);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Fetch notifications from last X days (or all if no daysLimit).
  // Liczba unread liczona lokalnie (`effectiveUnreadCount`) — uwzględnia
  // optymistyczne mark-as-read przy otwarciu popovera.
  const {
    notifications: rawNotifications = [],
    loading,
    error,
    hasMore,
  } = useInAppNotifications(
    currentUser?.uid,
    organizationId || undefined,
    daysLimit,
    // Tryb przycisku → paginacja serwerowa (10, 20, 30…). Popover → 500 dla trafnego licznika.
    loadMoreButton ? serverLimit : 500,
    showOnlyUnread,
  );

  // Po dojściu nowej porcji z Firebase (nowy snapshot) gasimy stan ładowania przycisku.
  useEffect(() => {
    setIsLoadingMore(false);
  }, [rawNotifications]);

  const { mutate: markAsRead } = useMarkNotificationAsRead();

  /**
   * Optymistyczne ID-y „przeczytanych w tej sesji" — pozwala UI od razu
   * pokazać badge = 0 i wszystkie pozycje jako przeczytane, nie czekając
   * na potwierdzenie z Firestore. Snapshot serwera (z `useInAppNotifications`)
   * dorówna stan po kilkuset ms, ale dla użytkownika zmiana jest natychmiastowa.
   */
  const [optimisticReadIds, setOptimisticReadIds] = useState<Set<string>>(new Set());
  /** Flag — auto-mark odpaliło się już raz w tym cyklu życia komponentu. */
  const hasAutoMarkedRef = useRef(false);

  // Reset wyświetlanego okna infinite-scroll gdy zbiór się zmienia.
  useEffect(() => {
    setDisplayCount(10);
  }, [rawNotifications.length]);

  /**
   * Auto-mark-as-read przy otwarciu popovera (happy scenario):
   *   1) Lokalnie oznaczamy WSZYSTKIE nieprzeczytane jako read przez
   *      dodanie ich ID do `optimisticReadIds` → UI natychmiast pokazuje
   *      0 unread + wszystkie kropki znikają.
   *   2) W tle (fire-and-forget) wysyłamy mutację per-notification do
   *      Firestore. Błędy są logowane ale NIE blokują UX — w najgorszym
   *      wypadku snapshot przy następnym otwarciu pokaże ten sam stan,
   *      a backend dostanie retry przez kolejny markAsRead.
   * Odpala się TYLKO raz na cykl życia komponentu (popover może się
   * remontować — `hasAutoMarkedRef` zabezpiecza przed re-firingiem).
   */
  useEffect(() => {
    if (hasAutoMarkedRef.current) return;
    if (!currentUser?.uid) return;
    if (loading) return;
    if (rawNotifications.length === 0) return;
    const unread = rawNotifications.filter((n) => !n.isRead);
    if (unread.length === 0) return;

    hasAutoMarkedRef.current = true;
    // 1) Lokalny optymistyczny update — instant feedback.
    setOptimisticReadIds((prev) => {
      const next = new Set(prev);
      unread.forEach((n) => next.add(n.id));
      return next;
    });
    // 2) Background batch — fire-and-forget per dokument. Jeśli któryś
    //    request padnie, nie obchodzi nas — kolejne otwarcie spróbuje
    //    ponownie (snapshot pokaże isRead=false → wpadnie do nowego batcha).
    const uid = currentUser.uid;
    unread.forEach((n) => markAsRead({ notificationId: n.id, userId: uid }));
  }, [currentUser?.uid, loading, rawNotifications, markAsRead]);

  /** Ręczny przycisk "Oznacz wszystkie" w stopce popovera — zostaje jako
   * fallback dla scenariusza gdy snapshot dorzucił nowe powiadomienia
   * podczas otwartego popovera. */
  const handleMarkAllAsRead = useCallback(() => {
    if (!currentUser?.uid) return;
    const uid = currentUser.uid;
    const unread = rawNotifications.filter((n) => !n.isRead && !optimisticReadIds.has(n.id));
    if (unread.length === 0) return;
    setOptimisticReadIds((prev) => {
      const next = new Set(prev);
      unread.forEach((n) => next.add(n.id));
      return next;
    });
    unread.forEach((n) => markAsRead({ notificationId: n.id, userId: uid }));
  }, [currentUser?.uid, rawNotifications, markAsRead, optimisticReadIds]);

  // Map notifications to UI format - only display first N notifications
  // Sort: unread first, then by date. Optymistycznie przeczytane (`optimisticReadIds`)
  // traktujemy jako `isRead=true` — frontend pokazuje je odznaczone od razu,
  // nawet jeśli serwer jeszcze nie potwierdził mutacji.
  const notifications = useMemo(
    () => {
      try {
        const isEffectivelyRead = (n: InAppNotification): boolean =>
          n.isRead || optimisticReadIds.has(n.id);

        // Sort: unread first, then by creation date
        const sorted = [...rawNotifications].sort((a, b) => {
          const aRead = isEffectivelyRead(a);
          const bRead = isEffectivelyRead(b);
          if (aRead !== bRead) return aRead ? 1 : -1;
          return 0;
        });

        // W trybie serwerowym pokazujemy wszystko, co pobrał Firebase (limit robi paginację).
        const sliced = loadMoreButton ? sorted : sorted.slice(0, displayCount);
        return sliced.map((n) => {
          const ui = mapInAppNotificationToUI(n, userLanguage || 'pl');
          return optimisticReadIds.has(n.id) ? { ...ui, isRead: true } : ui;
        });
      } catch (err) {
        return [];
      }
    },
    [rawNotifications, displayCount, userLanguage, optimisticReadIds, loadMoreButton],
  );

  /** Liczba nieprzeczytanych po uwzględnieniu optymistycznego stanu — UI badge
   * spada do 0 od razu po otwarciu popovera. */
  const effectiveUnreadCount = useMemo(
    () => rawNotifications.filter((n) => !n.isRead && !optimisticReadIds.has(n.id)).length,
    [rawNotifications, optimisticReadIds],
  );

  const isEmpty = rawNotifications.length === 0;

  // Doczytywanie przy scrollu (ten sam mechanizm co dziennik aktywności) —
  // odsłaniamy kolejne 10 pozycji. Reveal jest natychmiastowy, bo dane są już
  // w pamięci, więc bez sztucznego opóźnienia i spinnera.
  const handleReachEnd = () => {
    setDisplayCount((prev) => (prev < rawNotifications.length
      ? Math.min(prev + 10, rawNotifications.length)
      : prev));
  };

  useLoadMoreOnScroll(feedRef, handleReachEnd, {
    // W trybie przycisku „Załaduj więcej" nie doczytujemy przy scrollu.
    enabled: !isEmpty && !loadMoreButton,
    pageScroll,
    recheckKey: displayCount,
  });

  // Kolejny request do Firebase — zwiększamy limit o 10 (paginacja serwerowa).
  const handleLoadMoreServer = () => {
    setIsLoadingMore(true);
    setServerLimit((prev) => prev + PAGE_SIZE);
  };

  const hasMoreToShow = loadMoreButton
    ? hasMore
    : displayCount < rawNotifications.length;

  const handleNotificationClick = (
    notificationId: string,
    isRead: boolean,
    metadata?: InAppNotification['metadata'],
  ) => {
    // Mark as read if not already
    if (!isRead && currentUser?.uid) {
      markAsRead({
        notificationId,
        userId: currentUser.uid,
      });
    }

    // Navigate based on notification type
    if (metadata?.taskId) {
      // Navigate to new reports with taskId as query param to expand it
      navigate(`/reports?taskId=${metadata.taskId}`);
    } else if (metadata?.orderType === 'room-service' && metadata?.orderId) {
      // Navigate to room service orders with order ID as query param
      navigate(`/orders?orderId=${metadata.orderId}`);
    } else if (metadata?.orderType === 'products-services' && metadata?.orderId) {
      // Navigate to products services orders with order ID as query param
      navigate(`/order-products?orderId=${metadata.orderId}`);
    } else if (metadata?.orderType === 'event' && metadata?.eventId) {
      // Navigate to event registrations with registrationId to highlight it
      const registrationId = metadata?.registrationId;
      navigate(`/events/register/${metadata.eventId}${registrationId ? `?registrationId=${registrationId}` : ''}`);
    } else if (metadata?.orderType === 'survey' && metadata?.surveyId) {
      // Navigate to survey responses with answerId to highlight it
      const answerId = metadata?.answerId;
      navigate(`/surveys/${metadata.surveyId}/responses${answerId ? `?answerId=${answerId}` : ''}`);
    } else if (metadata?.orderType === 'support' && metadata?.requestId) {
      // Navigate to support and open the replies modal for this request
      navigate(`/support-service?request=${metadata.requestId}`);
    }
    // Add more navigation cases as needed
  };

  // Show loading when org is loading or notifications are loading
  if (loading || isOrgLoading) {
    return (
      <NotificationWrapper $fullWidth={fullWidth}>
        {showTitle && (
          <NotificationHeader>{t('alerts.notifications')}</NotificationHeader>
        )}
        <div className="flex items-center justify-center" style={{ minHeight: '200px', padding: '32px' }}>
          <Spinner size="md" color="primary" />
        </div>
      </NotificationWrapper>
    );
  }

  if (error) {
    return (
      <NotificationWrapper $fullWidth={fullWidth}>
        {showTitle && (
          <NotificationHeader>{t('alerts.notifications')}</NotificationHeader>
        )}
        <div className="flex flex-col items-center justify-center gap-2" style={{ minHeight: '200px', padding: '32px' }}>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('alerts.errorLoadingNotifications') || 'Błąd ładowania powiadomień'}
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500">
            {error.message || 'Spróbuj odświeżyć stronę'}
          </p>
        </div>
      </NotificationWrapper>
    );
  }

  // Fallback - jeśli nie ma currentUser lub organizationId
  if (!currentUser?.uid) {
    return (
      <NotificationWrapper $fullWidth={fullWidth}>
        {showTitle && (
          <NotificationHeader>{t('alerts.notifications')}</NotificationHeader>
        )}
        <div className="flex items-center justify-center" style={{ minHeight: '200px', padding: '32px' }}>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('alerts.notLoggedIn') || 'Zaloguj się, aby zobaczyć powiadomienia'}
          </p>
        </div>
      </NotificationWrapper>
    );
  }

  const listContent = (
    <NotificationList>
      {isEmpty ? (
        <TableEmptyState
          variant="activity"
          title={t('alerts.noNotifications')}
          description={t('alerts.noNotificationsDescription')}
        />
      ) : (
        notifications.map((notification) => {
          const Icon = notification.icon;

          return (
            <NotificationItem
              key={notification.id}
              $isRead={notification.isRead}
              $transparent={transparent}
              onClick={() => handleNotificationClick(notification.id, notification.isRead, notification.metadata)}
              style={{ cursor: 'pointer' }}
            >
              {!notification.isRead && <UnreadBadge />}

              <IconWrapper color={notification.bgColor}>
                <Icon size={14} color={notification.iconColor} strokeWidth={2} />
              </IconWrapper>

              <NotificationContent>
                <NotificationTitle $transparent={transparent}>
                  {notification.title}
                  {/* Odbiór osobisty nie ma numeru pokoju — guest-hub wysyła tam tag,
                      a „Pokój TAKE_TO_ROOM" nic obsłudze nie mówiło. */}
                  {isSelfPickup(notification.roomNumber) && (
                    <PickupTag>{t('orders.selfPickup', 'Odbiór własny')}</PickupTag>
                  )}
                  {notification.roomNumber && !isSelfPickup(notification.roomNumber) && (
                    <RoomNumber>
                      ·
                      {' '}
                      {t('alerts.room')}
                      {' '}
                      {notification.roomNumber}
                    </RoomNumber>
                  )}
                </NotificationTitle>
                <NotificationDescription $transparent={transparent}>
                  {/* Znacznik z treści pusha byłby powtórzeniem plakietki obok tytułu. */}
                  {notification.description.replace('[TAKE TO ROOM]', '').trim()}
                </NotificationDescription>
                <NotificationTime $transparent={transparent}>{notification.time}</NotificationTime>
              </NotificationContent>
            </NotificationItem>
          );
        })
      )}
    </NotificationList>
  );

  return (
    <NotificationWrapper $fullWidth={fullWidth} $transparent={transparent}>
      {showTitle && (
        <NotificationHeader>
          {t('alerts.notifications')}
          {effectiveUnreadCount > 0 && (
            <Chip color="danger" size="sm" variant="flat">
              {effectiveUnreadCount}
            </Chip>
          )}
        </NotificationHeader>
      )}

      {canAskForNotifications && (
        <PermissionPrompt>
          {t(
            'alerts.enablePrompt',
            'Włącz powiadomienia systemowe, żeby nie przegapić zamówienia przy panelu w tle.',
          )}
          <PermissionButton type="button" onClick={() => { requestNotifications().catch(() => undefined); }}>
            {t('alerts.enable', 'Włącz')}
          </PermissionButton>
        </PermissionPrompt>
      )}

      {pageScroll ? (
        <div ref={feedRef}>{listContent}</div>
      ) : (
        <ScrollShadow
          ref={feedRef}
          hideScrollBar
          style={{ maxHeight }}
          orientation="vertical"
        >
          {listContent}
        </ScrollShadow>
      )}

      {loadMoreButton && !isEmpty && hasMoreToShow && (
        <LoadMoreRow>
          <LoadMoreButton type="button" onClick={handleLoadMoreServer} disabled={isLoadingMore}>
            {isLoadingMore && <Spinner size="sm" color="current" />}
            {t('notificationsCenter.loadMore', 'Załaduj więcej')}
          </LoadMoreButton>
        </LoadMoreRow>
      )}

      {showFooter && !isEmpty && (
        <NotificationFooter>
          <FooterGhostButton
            onClick={handleMarkAllAsRead}
            disabled={effectiveUnreadCount === 0}
            type="button"
          >
            {t('alerts.markAllAsReadShort', 'Odznacz wszystko')}
          </FooterGhostButton>
          <FooterButton onClick={() => navigate('/centrum-powiadomien')}>
            {t('alerts.showAll')}
          </FooterButton>
        </NotificationFooter>
      )}
    </NotificationWrapper>
  );
};
